import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import CabBookingForm from '@/components/booking/CabBookingForm';
import AtithiNavbar from '@/components/atithi/AtithiNavbar';
import AtithiFooter from '@/components/atithi/AtithiFooter';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { BRAND } from '@/theme/brand';
import { SITE_ORIGIN, routePageUrl } from '@/config/site';
import { useCabRates } from '@/hooks/useCabRates';
import { formatVehicleTierLabel } from '@/lib/vehicleTierLabels';

const FARE_ESTIMATE_BUFFER_KM = 20;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    amount,
  );

const buildJsonLd = (route) => {
  const canonical = routePageUrl(route.slug);
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'LocalBusiness',
        '@id': `${SITE_ORIGIN}/#localbusiness`,
        name: `${BRAND.name} ${BRAND.tagline}`,
        description: route.metaDescription,
        url: SITE_ORIGIN,
        telephone: BRAND.contact.phone,
        email: BRAND.contact.email,
        address: {
          '@type': 'PostalAddress',
          streetAddress: BRAND.contact.address,
          addressRegion: 'Gujarat',
          addressCountry: 'IN',
        },
        areaServed: {
          '@type': 'State',
          name: 'Gujarat',
        },
      },
      {
        '@type': 'Service',
        name: route.h1,
        description: route.introText,
        url: canonical,
        provider: { '@id': `${SITE_ORIGIN}/#localbusiness` },
        areaServed: route.toCity
          ? [
              { '@type': 'City', name: route.fromCity },
              { '@type': 'City', name: route.toCity },
            ]
          : { '@type': 'State', name: 'Gujarat' },
        offers: {
          '@type': 'Offer',
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
          url: canonical,
        },
      },
    ],
  };
};

const RouteLandingPage = ({ route }) => {
  const { fares, loading } = useCabRates();
  const canonical = routePageUrl(route.slug);

  const fareEstimateDistanceKm =
    route.approxDistanceKm != null ? route.approxDistanceKm + FARE_ESTIMATE_BUFFER_KM : null;

  const fareRows = useMemo(() => {
    const bookable = fares.filter((f) => f.show_in_booking);
    const filtered = route.fareSlugs?.length
      ? bookable.filter((f) => route.fareSlugs.includes(f.slug))
      : bookable;

    return filtered.map((fare) => {
      const estimated =
        fareEstimateDistanceKm != null
          ? Math.round(fareEstimateDistanceKm * fare.rate_per_km)
          : null;
      return { ...fare, estimated };
    });
  }, [fares, fareEstimateDistanceKm, route.fareSlugs]);

  const tripSummary =
    route.pageType === 'vehicle'
      ? 'Group & outstation rentals across Gujarat'
      : route.pageType === 'airport'
        ? 'Airport pickup & drop — SVPIA Ahmedabad'
        : `${route.fromCity} → ${route.toCity}`;

  return (
    <div className="page-wrapper min-h-screen bg-atithi-bg font-body">
      <Helmet>
        <title>{route.title}</title>
        <meta name="description" content={route.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={route.title} />
        <meta property="og:description" content={route.metaDescription} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(buildJsonLd(route))}</script>
      </Helmet>

      <AtithiNavbar />

      <section className="bg-atithi-navy text-white pt-28 pb-14 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E8651A] mb-3">
            {tripSummary}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display leading-tight mb-4">
            {route.h1}
          </h1>
          {(route.approxDistanceKm || route.approxDuration) && (
            <p className="text-white/85 text-sm sm:text-base font-body">
              {route.approxDistanceKm != null && (
                <span className="inline-block mr-4">~{route.approxDistanceKm} km</span>
              )}
              {route.approxDuration && (
                <span className="inline-block">Typical duration: {route.approxDuration}</span>
              )}
            </p>
          )}
        </div>
      </section>

      <section className="section-standard px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <p className="section-subtext text-base sm:text-lg">{route.introText}</p>
          <ul className="mt-6 grid sm:grid-cols-3 gap-4 text-sm font-body">
            {[
              '✓ Transparent per-km pricing',
              '✓ AC fleet · verified drivers',
              '✓ WhatsApp quote in minutes',
            ].map((item) => (
              <li key={item} className="bg-[#F8F4EE] rounded-lg px-4 py-3 text-[#1B2D5E] font-medium">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="booking-form" className="section-standard px-6 bg-[#F8F4EE]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="section-label">BOOK NOW</p>
            <h2 className="section-heading mt-2">Get Your Fare on WhatsApp</h2>
            <p className="section-subtext mt-2 max-w-xl mx-auto">
              Fill in your trip details — we reply with a confirmed quote. No booking fee.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-atithi-border shadow-xl p-6 lg:p-8">
            <CabBookingForm
              key={`${route.slug}-${route.initialTripType}-${route.fromCity}-${route.toCity || ''}`}
              initialTripType={route.initialTripType}
              initialPickup={route.fromCity || ''}
              initialDrop={route.toCity || ''}
              leadSource={route.leadSource}
            />
          </div>
        </div>
      </section>

      <section className="section-standard px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="section-label">FARE GUIDE</p>
            <h2 className="section-heading mt-2">
              {fareEstimateDistanceKm != null ? 'Estimated One-Way Fares' : 'Per-Km Rates'}
            </h2>
            <p className="section-subtext mt-2">
              {fareEstimateDistanceKm != null
                ? `Indicative fares for ~${fareEstimateDistanceKm} km. Toll, state tax & parking extra.`
                : 'Rates below are per kilometre. Final fare confirmed on WhatsApp.'}
            </p>
          </div>

          {loading ? (
            <p className="text-center text-gray-500 font-body text-sm">Loading fares…</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-[#1B2D5E] text-white text-left">
                    <th className="px-4 py-3 font-semibold">Vehicle</th>
                    <th className="px-4 py-3 font-semibold">Seats</th>
                    <th className="px-4 py-3 font-semibold">Rate/km</th>
                    {fareEstimateDistanceKm != null && (
                      <th className="px-4 py-3 font-semibold">Est. one-way</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {fareRows.map((row, i) => (
                    <tr
                      key={row.slug}
                      className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F4EE]'}
                    >
                      <td className="px-4 py-3 font-medium text-[#1B2D5E]">
                        {formatVehicleTierLabel(row.name)}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{row.seats}</td>
                      <td className="px-4 py-3 text-gray-600">₹{row.rate_per_km}/km</td>
                      {fareEstimateDistanceKm != null && (
                        <td className="px-4 py-3 font-semibold text-[#E8651A]">
                          {row.estimated != null ? `~${formatCurrency(row.estimated)}` : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-4 text-xs text-gray-500 font-body leading-relaxed">
            * Fares are indicative and subject to route, vehicle availability, night charges, and
            applicable tolls/taxes. Submit the form above for an exact quote.
          </p>
        </div>
      </section>

      <section className="section-standard px-6 bg-[#F8F4EE]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <p className="section-label">FAQ</p>
            <h2 className="section-heading mt-2">Common Questions</h2>
          </div>
          <Accordion type="single" collapsible className="bg-white rounded-xl border border-gray-200 px-4">
            {route.faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`faq-${index}`}>
                <AccordionTrigger className="text-[#1B2D5E] font-semibold font-body text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 font-body leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <AtithiFooter />
    </div>
  );
};

export default RouteLandingPage;
